import { Pagination } from '../commons/pagination';

export interface RiskCriterionResponse {
  data: RiskCriterion[];
  status: number;
  pagination: Pagination;
  success: boolean;
}

export interface RiskCriterion {
  criterionId?: number;
  code: string;
  name: string;
  description?: string | null;
  weight: number;
  critical: boolean;
  enabled: boolean;
  displayOrder: number;
  ruleSteps: RuleStep[];
}

export enum MissingPolicy {
  BLOCK = 'BLOCK',
  DEFAULT_SCORE = 'DEFAULT_SCORE',
  DEFAULT_NEXT_CATALOG = 'DEFAULT_NEXT_CATALOG',
}

export interface RuleStep {
  stepId?: number;
  stepOrder: number;
  inputAttribute: string;
  catalogCode?: string | null;
  catalogId?: number | null;
  matchField: string;
  scoreField?: string | null;
  nextCatalogField?: string | null;
  missingPolicy: MissingPolicy;
  defaultScore?: number | null;
  defaultNextCatalogCode?: string | null;
  defaultNextCatalog?: number | null;
  nextStepOrder?: number | null;
  producesScore: boolean;
  enabled: boolean;
  comparisonType: string;
  dataType: string;
  rangeEndField?: string | null;
}

export interface RiskLevelResponse {
  data: RiskLevel[];
  status: number;
  pagination: Pagination;
  success: boolean;
}

export interface RiskLevel {
  levelId?: number;
  code: string;
  name: string;
  color?: string;
  minScore: number;
  maxScore: number;
  priority?: number;
}

export enum MatrixStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface RiskMatrixResponse {
  data: RiskMatrix[];
  status: number;
  pagination: Pagination;
  success: boolean;
}

export interface RiskMatrix {
  matrixId: number;
  investmentOfficeId: string;
  code: string;
  version: number;
  status?: MatrixStatus;
  monitoringUnit?: string;
  monitoringWindow?: number;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  criteria?: RiskCriterion[];
  levels?: RiskLevel[];
  ruleSteps?: RuleStep[];
}

export enum ExecutionStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  PARTIAL = 'PARTIAL',
  FAILED = 'FAILED',
}

export interface RiskExecutionResponse {
  data: RiskExecution[];
  status: number;
  pagination: Pagination;
  success: boolean;
}

export interface RiskExecution {
  executionId: number;
  folio: string;
  investmentOfficeId: string;
  matrixId: number;
  matrixVersion?: number;
  clientId?: number;
  totalSubjects?: number;
  processedSubjects?: number;
  failedSubjects?: number;
  blockedSubjects?: number;
  status: ExecutionStatus;
  startedAt?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  errorMessage?: string | null;
}

export interface RiskExecutionDetailResponse {
  data: RiskExecutionDetail[];
  pagination: Pagination;
  status: number;
  success: boolean;
}

export interface RiskExecutionDetail extends RiskExecution {
  test?: string;
}

export interface RiskClientResultResponse {
  data: RiskClientResult[];
  pagination: Pagination;
  status: number;
  success: boolean;
}

export enum RunStatus {
  OK = 'OK',
  BLOCKED = 'BLOCKED',
  ERROR = 'ERROR',
}

export interface RiskClientResult extends KycPersonDto {
  runId?: number;
  matrixId?: number;
  subjectId?: string;
  investmentOfficeId?: string;
  startedAt?: string; // formato ISO (YYYY-MM-DDTHH:mm:ss)
  finishedAt?: string; // formato ISO (YYYY-MM-DDTHH:mm:ss)
  status?: RunStatus; // equivalente a RunStatus
  totalScore?: number;
  levelCode?: string;
  blockReason?: string;
}

export interface KycPersonDto {
  clientId?: string;
  cveInternal?: string;
  investmentOfficeId?: string;
  fullName?: string;
  rfc?: string;
  typePersonCode?: string;
  typePersonDescription?: string;
  birthDate?: string;
  occupationCode?: string;
  occupationDescription?: string;
  stateCode?: string;
  stateDescription?: string;
  serviceTypeCode?: string;
  serviceTypeDescription?: string;
  nationalityCode?: string;
  nationalityDescription?: string;
  totalDeposits?: string;
  totalWithdrawals?: string;
  avgMonthlyDeposits?: string;
  avgMonthlyWithdrawals?: string;
  accounts?: KycInvestmentAccountDto[];
}

export interface KycInvestmentAccountDto {
  accountNumber?: string;
  statusCode?: string;
  statusDescription?: string;
  brokerageHouseCode?: string;
  brokerageHouseDescription?: string;
}

export interface RiskCriterionData {
  matrixId?: number;
  criteria?: RiskCriterion[];
  totalCount?: number;
  totalWeight?: number;
  criticalCount?: number;
  disabledCount?: number;
}

export interface RiskCriterionDataResponse {
  data: RiskCriterionData;
  status: number;
  pagination: Pagination;
  success: boolean;
}
